import React from 'react';
import useSWR from 'swr';
import { apiClient } from '../../services/apiClient';

interface Broadcast {
  broadcastId: string;
  title: string;
  body: string;
  type: string;
  adminId: string;
  createdAt: number;
}

function formatTime(ts: number): string {
  if (!ts) return '';
  const now = Date.now();
  const diff = now - ts;
  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
  return new Date(ts).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TYPE_ICONS: Record<string, string> = {
  announcement: '📢',
  update: '🆕',
  maintenance: '🔧',
  promotion: '🎉',
};

export const SystemChatWindow: React.FC = () => {
  const { data: broadcasts } = useSWR<Broadcast[]>(
    'broadcasts-list',
    async () => {
      const res = await apiClient.get<{ success: boolean; data: { broadcasts: Broadcast[] } }>('/api/v1/broadcasts');
      return (res.data as any)?.data?.broadcasts || (res.data as any)?.broadcasts || [];
    },
    { revalidateOnFocus: false, dedupingInterval: 30000 }
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <span className="text-lg">📢</span>
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">系统消息</h3>
          <p className="text-xs text-gray-400">来自平台的公告和通知</p>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {!broadcasts || broadcasts.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">暂无系统消息</div>
        ) : (
          broadcasts.map((b) => (
            <div key={b.broadcastId} className="flex gap-3 max-w-[85%]">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-sm">{TYPE_ICONS[b.type] || '📢'}</span>
              </div>
              <div>
                <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <p className="text-sm font-medium text-gray-900">{b.title}</p>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{b.body}</p>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 ml-1">{formatTime(b.createdAt)}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Read-only footer */}
      <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
        <p className="text-xs text-gray-400 text-center">系统消息仅供查看，无法回复</p>
      </div>
    </div>
  );
};
